import { motion } from "framer-motion";
import { Sparkles, ShoppingBag, Compass, FileText, Users, ChefHat } from "lucide-react";
import { useUI } from "@/context/UIContext";

const SCENIC_BAND = "https://images.unsplash.com/photo-1551648746-d158bcd704e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxjYW1wZmlyZSUyMGxvZyUyMGNhYmluJTIwZ29sZGVuJTIwaG91cnxlbnwwfHx8fDE3ODY3MTY1NDN8MA&ixlib=rb-4.1.0&q=85&w=1600";

export function BrandLoader({ label = "Loading" }) {
  return (
    <div className="min-h-screen bg-paper grid place-items-center" data-testid="brand-loader">
      <div className="flex flex-col items-center gap-4">
        <motion.img
          src="/brand/mark_forest.png" alt="HomeRange" className="w-14 h-14 object-contain"
          initial={{ opacity: 0.5, scale: 0.92 }}
          animate={{ opacity: [0.5, 1, 0.5], scale: [0.92, 1, 0.92] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        />
        <span className="hr-overline text-saddle">{label}</span>
      </div>
    </div>
  );
}


export function SectionBanner({ overline, title, subtitle, image, testid = "section-banner" }) {
  return (
    <div className="relative -mx-5 sm:-mx-10 -mt-20 sm:-mt-28 mb-10 sm:mb-14 h-56 sm:h-72 overflow-hidden grain" data-testid={testid}>
      <img src={image} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/75 to-forest/25" />
      <div className="relative z-10 h-full max-w-[1500px] mx-auto px-5 sm:px-10 flex flex-col justify-end pb-7">
        <span className="hr-overline text-blaze">{overline}</span>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-bone mt-2 leading-none tracking-tight">{title}</h2>
        {subtitle && <p className="text-bone/75 mt-3 max-w-xl text-sm">{subtitle}</p>}
      </div>
    </div>
  );
}


export function FeatureStrip() {
  const { openAI } = useUI();
  const items = [
    { icon: Sparkles, title: "AI Hunting Assistant", sub: "Personal guidance 24/7", onClick: () => openAI() },
    { icon: ShoppingBag, title: "Gear & Marketplace", sub: "Compare. Shop. Save.", href: "/marketplace" },
    { icon: Compass, title: "Outfitters & Hunts", sub: "Find the right hunt.", href: "#hunts" },
    { icon: FileText, title: "Regs & Licenses", sub: "Stay legal. Stay informed.", href: "#regs" },
    { icon: Users, title: "Journal & Community", sub: "Stories. Connection. Legacy.", href: "#community" },
    { icon: ChefHat, title: "Recipes & Films", sub: "From camp to table.", href: "#recipes" },
  ];
  return (
    <section className="bg-bone px-5 sm:px-10 py-16 sm:py-20" data-testid="feature-strip">
      <div className="max-w-[1500px] mx-auto">
        <span className="hr-overline text-saddle">Everything for the hunt</span>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-forest mt-2 tracking-tight">All in one place.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {items.map((it, i) => {
            const Inner = (
              <motion.div
                initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
                className="group h-full bg-white border border-saddle/20 p-6 flex items-start gap-4 hover:-translate-y-1 hover:border-saddle/60 transition-[transform,border-color] duration-300"
              >
                <span className="shrink-0 w-12 h-12 grid place-items-center bg-forest text-bone group-hover:bg-blaze transition-colors">
                  <it.icon size={22} strokeWidth={1.75} />
                </span>
                <span>
                  <h3 className="font-serif text-xl text-forest leading-tight">{it.title}</h3>
                  <p className="text-sm text-charcoal/60 mt-1">{it.sub}</p>
                </span>
              </motion.div>
            );
            const testid = `feature-${it.title.toLowerCase().replace(/[^a-z]+/g, "-")}`;
            return it.href ? (
              <a key={it.title} href={it.href} className="block h-full" data-testid={testid}>{Inner}</a>
            ) : (
              <button key={it.title} onClick={it.onClick} className="block h-full text-left w-full" data-testid={testid}>{Inner}</button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function ScenicBand() {
  return (
    <section className="relative min-h-[46vh] flex items-center overflow-hidden grain" data-testid="scenic-band">
      <div className="absolute inset-0 z-0">
        <img src={SCENIC_BAND} alt="Log cabin and campfire at golden hour in the mountains" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/60 to-forest/20" />
      </div>
      <div className="relative z-10 max-w-[1500px] mx-auto w-full px-5 sm:px-10 py-24">
        <motion.h2
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="font-serif text-bone text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-2xl"
        >
          Everything for the hunt.<br /><span className="text-blaze">All in one place.</span>
        </motion.h2>
      </div>
    </section>
  );
}
